import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Color as TColor } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { FontFamily } from '@tiptap/extension-font-family';
import { FontSize } from '@tiptap/extension-font-size';
import { Image as ImageExt } from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Highlight from '@tiptap/extension-highlight';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import { useEffect, useCallback, useRef } from 'react';
import {
  Bold, Italic, Underline as UIcon, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Heading1, Heading2, Heading3,
  Quote, Minus, Undo2, Redo2, Link2, ImagePlus,
  Highlighter, Table as TableIcon, RemoveFormatting, Type, Palette,
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

const FONT_FAMILIES = [
  { label: 'Par défaut', value: '' },
  { label: 'Barlow (titres)', value: '"Barlow", sans-serif' },
  { label: 'Roboto', value: '"Roboto", sans-serif' },
  { label: 'Inter', value: '"Inter", sans-serif' },
  { label: 'Georgia (serif)', value: 'Georgia, "Times New Roman", serif' },
  { label: 'Courier (mono)', value: '"Courier New", monospace' },
  { label: 'Playfair (élégant)', value: '"Playfair Display", serif' },
];

const FONT_SIZES = [
  { label: 'XS', value: '11px' },
  { label: 'S', value: '13px' },
  { label: 'M', value: '15px' },
  { label: 'L', value: '18px' },
  { label: 'XL', value: '22px' },
  { label: '2XL', value: '28px' },
  { label: '3XL', value: '36px' },
];

const ToolBtn = ({ active, onClick, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`p-1.5 rounded-md transition-colors ${active ? 'bg-[#1e3a5f] text-white' : 'text-gray-600 hover:bg-gray-200'}`}
  >
    {children}
  </button>
);

const Sep = () => <div className="w-px h-5 bg-gray-200 mx-0.5" />;

const ColorPicker = ({ editor }) => {
  const colors = [
    '#1e3a5f', '#35AFE4', '#35afe4', '#045474',
    '#16a34a', '#27ae60', '#d97706', '#dc2626',
    '#8e44ad', '#7c3aed', '#000000', '#6b7280',
  ];
  return (
    <div className="relative group">
      <button type="button" className="p-1.5 rounded-md text-gray-600 hover:bg-gray-200 transition-colors flex items-center gap-0.5" title="Couleur du texte">
        <Type size={14} />
        <span className="w-2.5 h-2.5 rounded-full border border-gray-300"
          style={{ background: editor.getAttributes('textStyle').color || '#1e3a5f' }} />
      </button>
      <div className="absolute top-full left-0 mt-1 hidden group-hover:flex flex-col bg-white rounded-lg shadow-xl border border-gray-200 p-2 z-50">
        <div className="grid grid-cols-6 gap-1 mb-2">
          {colors.map(c => (
            <button key={c} type="button" onClick={() => editor.chain().focus().setColor(c).run()}
              className="w-5 h-5 rounded-full border border-gray-200 hover:scale-125 transition-transform"
              style={{ backgroundColor: c }} title={c} />
          ))}
        </div>
        <div className="flex items-center gap-1.5 border-t border-gray-100 pt-2">
          <Palette size={12} className="text-gray-400" />
          <input type="color"
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
            className="w-7 h-6 cursor-pointer border border-gray-200 rounded"
            title="Couleur personnalisée" />
          <button type="button"
            onClick={() => editor.chain().focus().unsetColor().run()}
            className="text-[10px] text-gray-500 hover:text-gray-800 underline">
            Réinitialiser
          </button>
        </div>
      </div>
    </div>
  );
};

const FontFamilySelect = ({ editor }) => {
  const current = editor.getAttributes('textStyle').fontFamily || '';
  return (
    <select
      value={current}
      onChange={(e) => {
        const val = e.target.value;
        if (val === '') editor.chain().focus().unsetFontFamily().run();
        else editor.chain().focus().setFontFamily(val).run();
      }}
      title="Police"
      className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white hover:bg-gray-50 outline-none focus:ring-1 focus:ring-[#35AFE4]"
      data-testid="editor-font-family"
    >
      {FONT_FAMILIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
    </select>
  );
};

const FontSizeSelect = ({ editor }) => {
  const current = editor.getAttributes('textStyle').fontSize || '';
  return (
    <select
      value={current}
      onChange={(e) => {
        const val = e.target.value;
        if (val === '') editor.chain().focus().unsetFontSize().run();
        else editor.chain().focus().setFontSize(val).run();
      }}
      title="Taille du texte"
      className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white hover:bg-gray-50 outline-none focus:ring-1 focus:ring-[#35AFE4] w-14"
      data-testid="editor-font-size"
    >
      <option value="">—</option>
      {FONT_SIZES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
    </select>
  );
};

const RichTextEditor = ({ value, onChange, placeholder, height = '200px' }) => {
  const isInternalUpdate = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, strike: { HTMLAttributes: {} } }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TColor.configure({ types: ['textStyle'] }),
      TextStyle,
      FontFamily.configure({ types: ['textStyle'] }),
      FontSize.configure({ types: ['textStyle'] }),
      ImageExt.configure({ inline: false, allowBase64: true }),
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder: placeholder || 'Saisissez votre contenu...' }),
      Highlight.configure({ multicolor: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => {
      isInternalUpdate.current = true;
      onChange(ed.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none px-4 py-3',
        style: `min-height: ${height}`,
      },
    },
  });

  useEffect(() => {
    if (editor && !isInternalUpdate.current && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
    }
    isInternalUpdate.current = false;
  }, [value, editor]);

  const handleImageUpload = useCallback(async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      if (!input.files[0]) return;
      const formData = new FormData();
      formData.append('file', input.files[0]);
      try {
        const res = await axios.post(`${API}/upload/cms-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        const url = res.data.url.startsWith('http') ? res.data.url : `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
        editor.chain().focus().setImage({ src: url }).run();
      } catch { /* ignore */ }
    };
    input.click();
  }, [editor]);

  const setLink = useCallback(() => {
    const url = window.prompt('URL du lien:', editor?.getAttributes('link').href || '');
    if (url === null) return;
    if (url === '') { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const addTable = useCallback(() => {
    editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="rich-editor-wrapper border border-gray-200 rounded-xl overflow-hidden bg-white" data-testid="rich-text-editor">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-gray-200 flex-wrap">
        <ToolBtn active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Titre 1"><Heading1 size={14} /></ToolBtn>
        <ToolBtn active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Titre 2"><Heading2 size={14} /></ToolBtn>
        <ToolBtn active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Titre 3"><Heading3 size={14} /></ToolBtn>
        <Sep />
        <FontFamilySelect editor={editor} />
        <FontSizeSelect editor={editor} />
        <Sep />
        <ToolBtn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Gras"><Bold size={14} /></ToolBtn>
        <ToolBtn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italique"><Italic size={14} /></ToolBtn>
        <ToolBtn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Souligné"><UIcon size={14} /></ToolBtn>
        <ToolBtn active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Barré"><Strikethrough size={14} /></ToolBtn>
        <ToolBtn active={editor.isActive('highlight')} onClick={() => editor.chain().focus().toggleHighlight({ color: '#fef08a' }).run()} title="Surligner"><Highlighter size={14} /></ToolBtn>
        <ColorPicker editor={editor} />
        <Sep />
        <ToolBtn active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Aligner à gauche"><AlignLeft size={14} /></ToolBtn>
        <ToolBtn active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Centrer"><AlignCenter size={14} /></ToolBtn>
        <ToolBtn active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Aligner à droite"><AlignRight size={14} /></ToolBtn>
        <ToolBtn active={editor.isActive({ textAlign: 'justify' })} onClick={() => editor.chain().focus().setTextAlign('justify').run()} title="Justifier"><AlignJustify size={14} /></ToolBtn>
        <Sep />
        <ToolBtn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Liste à puces"><List size={14} /></ToolBtn>
        <ToolBtn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Liste numérotée"><ListOrdered size={14} /></ToolBtn>
        <ToolBtn active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Citation"><Quote size={14} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Ligne horizontale"><Minus size={14} /></ToolBtn>
        <Sep />
        <ToolBtn active={editor.isActive('link')} onClick={setLink} title="Lien"><Link2 size={14} /></ToolBtn>
        <ToolBtn onClick={handleImageUpload} title="Insérer image"><ImagePlus size={14} /></ToolBtn>
        <ToolBtn onClick={addTable} title="Insérer tableau"><TableIcon size={14} /></ToolBtn>
        <Sep />
        <ToolBtn onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Effacer le formatage"><RemoveFormatting size={14} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().undo().run()} title="Annuler"><Undo2 size={14} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().redo().run()} title="Rétablir"><Redo2 size={14} /></ToolBtn>
      </div>

      {/* Editor */}
      <EditorContent editor={editor} />

      <style>{`
        .rich-editor-wrapper .ProseMirror { min-height: ${height}; }
        .rich-editor-wrapper .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #adb5bd;
          pointer-events: none;
          height: 0;
        }
        .rich-editor-wrapper .ProseMirror h1 { font-size: 1.5em; font-weight: 700; margin: 0.5em 0; }
        .rich-editor-wrapper .ProseMirror h2 { font-size: 1.25em; font-weight: 700; margin: 0.4em 0; }
        .rich-editor-wrapper .ProseMirror h3 { font-size: 1.1em; font-weight: 600; margin: 0.3em 0; }
        .rich-editor-wrapper .ProseMirror ul { list-style: disc; padding-left: 1.5em; }
        .rich-editor-wrapper .ProseMirror ol { list-style: decimal; padding-left: 1.5em; }
        .rich-editor-wrapper .ProseMirror blockquote { border-left: 3px solid #1e3a5f; padding-left: 1em; color: #555; margin: 0.5em 0; }
        .rich-editor-wrapper .ProseMirror hr { border: none; border-top: 2px solid #e5e7eb; margin: 1em 0; }
        .rich-editor-wrapper .ProseMirror a { color: #35AFE4; text-decoration: underline; }
        .rich-editor-wrapper .ProseMirror img { max-width: 100%; border-radius: 8px; margin: 0.5em 0; }
        .rich-editor-wrapper .ProseMirror mark { background-color: #fef08a; padding: 0 2px; border-radius: 2px; }
        .rich-editor-wrapper .ProseMirror table { border-collapse: collapse; width: 100%; margin: 0.5em 0; }
        .rich-editor-wrapper .ProseMirror th, .rich-editor-wrapper .ProseMirror td { border: 1px solid #d1d5db; padding: 6px 10px; text-align: left; }
        .rich-editor-wrapper .ProseMirror th { background: #f3f4f6; font-weight: 600; }
        .rich-editor-wrapper .ProseMirror:focus { outline: none; }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
